import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"

export default class extends Controller {
  static values = {
    data3m: Array,
    data1m: Array
  }

  connect() {
    const token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!token) {
      this.element.innerHTML = '<div class="alert alert-warning">Mapbox token not configured.</div>'
      return
    }
    mapboxgl.accessToken = token

    const data3m = this.data3mValue
    const data1m = this.data1mValue
    const allPoints = [...data3m, ...data1m]

    const center = allPoints.length > 0
      ? [
          allPoints.reduce((s, p) => s + p.lng, 0) / allPoints.length,
          allPoints.reduce((s, p) => s + p.lat, 0) / allPoints.length
        ]
      : [-98.5, 39.8]

    this.map = new mapboxgl.Map({
      container: this.element,
      style: "mapbox://styles/mapbox/dark-v11",
      center,
      zoom: allPoints.length > 0 ? 5 : 4
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    this.map.on("load", () => {
      if (data3m.length > 0) this.#addHeatLayer("3m", data3m, "rgba(255, 200, 0, ", 30, 15)
      if (data1m.length > 0) this.#addHeatLayer("1m", data1m, "rgba(255, 50, 0, ", 25, 12)
      this.#addPins(allPoints)

      if (allPoints.length > 1) {
        const bounds = new mapboxgl.LngLatBounds()
        allPoints.forEach(p => bounds.extend([p.lng, p.lat]))
        this.map.fitBounds(bounds, { padding: 80 })
      }
    })
  }

  disconnect() {
    this.map?.remove()
  }

  #addHeatLayer(id, data, colorBase, outerBase, innerBase) {
    const source = `activity-${id}`
    this.map.addSource(source, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: data.map(p => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
          properties: { count: p.count, title: p.title, city: p.city }
        }))
      }
    })

    // Outer glow
    this.map.addLayer({
      id: `heat-${id}-outer`,
      type: "circle",
      source,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["get", "count"], 1, outerBase, 3, outerBase * 1.7, 5, outerBase * 2.3, 10, outerBase * 3.3],
        "circle-color": `${colorBase}0.15)`,
        "circle-blur": 0.8
      }
    })

    // Inner glow
    this.map.addLayer({
      id: `heat-${id}-inner`,
      type: "circle",
      source,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["get", "count"], 1, innerBase, 3, innerBase * 1.7, 5, innerBase * 2.7, 10, innerBase * 4],
        "circle-color": ["interpolate", ["linear"], ["get", "count"],
          1, `${colorBase}0.25)`,
          3, `${colorBase}0.4)`,
          5, `${colorBase}0.55)`,
          10, `${colorBase}0.7)`
        ],
        "circle-blur": 0.6
      }
    })
  }

  #addPins(allPoints) {
    const unique = [...new Map(allPoints.map(p => [`${p.lat},${p.lng}`, p])).values()]
    if (!unique.length) return

    this.map.addSource("pins", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: unique.map(p => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [p.lng, p.lat] },
          properties: { title: p.title, city: p.city, count: p.count }
        }))
      }
    })

    this.map.addLayer({
      id: "pins",
      type: "circle",
      source: "pins",
      paint: {
        "circle-radius": 5,
        "circle-color": "#48BA16",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff"
      }
    })

    this.map.on("click", "pins", (e) => {
      const { title, city, count } = e.features[0].properties
      new mapboxgl.Popup()
        .setLngLat(e.features[0].geometry.coordinates)
        .setHTML(`<div style="background:#252829;color:#dedcd9;padding:4px"><strong>${title}</strong><br><span style="color:#a0a0a0">${city}</span><br><span style="color:#a0a0a0">${count} reservation(s)</span></div>`)
        .addTo(this.map)
    })

    this.map.on("mouseenter", "pins", () => this.map.getCanvas().style.cursor = "pointer")
    this.map.on("mouseleave", "pins", () => this.map.getCanvas().style.cursor = "")
  }
}
